import React, { useState, useEffect, useRef } from 'react';
import { Box, Container, Typography, Button, Grid, Card, CardContent } from '@mui/material';
import { 
  CheckCircle, 
  ArrowForward, 
  Info,
  AccountTree as NetworkIcon,
  LocalHospital as HospitalIcon,
  Groups as GroupsIcon,
  Business as BusinessIcon,
  MedicalServices as MedicalServicesIcon
} from '@mui/icons-material';
import { useThemeMode } from '../../common/contexts/ThemeContext';
import NavigationBar from '../../components/NavigationBar';
import AnimatedTextCarousel from '../../components/AnimatedTextCarousel';

/**
 * LandingPage Component
 * 
 * A sleek, modern landing page with visual effects and animations.
 * Based on the provided screenshots with enhanced styling.
 */
const LandingPage: React.FC = () => {
  const { mode } = useThemeMode();
  const isDark = mode === 'dark';

  const bgColor = isDark ? '#000000' : '#F5F5F7';
  const textColor = isDark ? '#FFFFFF' : '#1D1D1F';
  const primaryBlue = '#007AFF';
  const lightBlue = '#7AF0FF';
  const darkBlue = isDark ? '#FFFFFF' : '#1D1D1F'; // White in dark mode, dark in light mode
  const darkBlueText = isDark ? '#E0E0E0' : '#003D7A'; // Light gray in dark mode, dark blue in light mode
  const cardBg = isDark ? 'rgba(255, 255, 255, 0.05)' : '#FFFFFF';

  // State for auto-highlighting the bottom line cards
  const [highlightedCardIndex, setHighlightedCardIndex] = useState(-1);
  const [hasAnimated, setHasAnimated] = useState(false);
  const bottomLineSectionRef = useRef<HTMLDivElement>(null);

  // State for animating the "Built for Leaders At" cards
  const [visibleLeaderCards, setVisibleLeaderCards] = useState<number[]>([]);
  const [hasAnimatedLeaders, setHasAnimatedLeaders] = useState(false);
  const leadersSectionRef = useRef<HTMLDivElement>(null);

  // Animate cards once when section comes into view
  useEffect(() => {
    if (hasAnimated || !bottomLineSectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            setHasAnimated(true);
            // Animate through cards: 0 -> 1 -> 2
            setHighlightedCardIndex(0);
            setTimeout(() => setHighlightedCardIndex(1), 400);
            setTimeout(() => setHighlightedCardIndex(2), 800);
            setTimeout(() => setHighlightedCardIndex(-1), 1200); // Reset after animation
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of section is visible
    );

    observer.observe(bottomLineSectionRef.current);

    return () => {
      if (bottomLineSectionRef.current) {
        observer.unobserve(bottomLineSectionRef.current);
      }
    };
  }, [hasAnimated]);

  // Animate leader cards once when section comes into view
  useEffect(() => {
    if (hasAnimatedLeaders || !leadersSectionRef.current) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimatedLeaders) {
            setHasAnimatedLeaders(true);
            // Animate cards appearing one after another
            const leaderCards = [0, 1, 2, 3, 4];
            leaderCards.forEach((index) => {
              setTimeout(() => {
                setVisibleLeaderCards((prev) => [...prev, index]);
              }, index * 200); // 200ms delay between each card
            });
          }
        });
      },
      { threshold: 0.3 } // Trigger when 30% of section is visible
    );

    observer.observe(leadersSectionRef.current);

    return () => {
      if (leadersSectionRef.current) {
        observer.unobserve(leadersSectionRef.current);
      }
    };
  }, [hasAnimatedLeaders]);

  return (
    <Box
      sx={{
        minHeight: '100vh',
        backgroundColor: bgColor,
        color: textColor,
        pt: 10,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '100%',
          background: isDark
            ? 'radial-gradient(circle at 20% 50%, rgba(0, 122, 255, 0.1) 0%, transparent 50%)'
            : 'radial-gradient(circle at 20% 50%, rgba(0, 122, 255, 0.05) 0%, transparent 50%)',
          pointerEvents: 'none',
          zIndex: 0,
        },
      }}
    >
      <NavigationBar />

      <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Hero Section with Headline and Capital Planning GIF */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            py: { xs: 6, md: 10 },
            animation: 'fadeInUp 0.8s ease-out',
            '@keyframes fadeInUp': {
              from: {
                opacity: 0,
                transform: 'translateY(30px)',
              },
              to: {
                opacity: 1,
                transform: 'translateY(0)',
              },
            },
          }}
        >
          {/* Headline */}
          <Typography
            variant="h1"
            component="h1"
            sx={{
              fontSize: { xs: '1.25rem', sm: '1.75rem', md: '2.75rem', lg: '3.5rem' },
              fontWeight: 700,
              mb: 4,
              color: darkBlue,
              lineHeight: 1.3,
              whiteSpace: { xs: 'normal', sm: 'normal', md: 'nowrap' },
              px: { xs: 2, sm: 0 },
              background: isDark
                ? 'linear-gradient(135deg, #FFFFFF 0%, #7AF0FF 100%)'
                : 'linear-gradient(135deg, #1D1D1F 0%, #007AFF 100%)',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            AI-Powered Strategic Intelligence for Healthcare Leaders.
          </Typography>

          {/* Animated Text Carousel */}
          <Box
            sx={{
              mb: 8,
              width: '100%',
              maxWidth: '900px',
              px: 3,
            }}
          >
            <AnimatedTextCarousel
              texts={[
                'Value Based Care Transformation',
                'Capital Planning',
                'Site Strategy',
                'Competitive Positioning',
                'Workforce Development',
                'Market Expansion',
                'Operational Enhancements',
                'M&A',
                'Payor Strategy',
                'Population Health Management',
                'Network Optimization',
              ]}
                interval={1500}
              fontSize={{ xs: '1.75rem', md: '2.5rem' }}
              fontWeight={700}
              color={primaryBlue}
            />
          </Box>

          {/* Section 1: Main Headline with Three Features */}
          <Box sx={{ width: '100%', mb: 10 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.75rem' },
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: darkBlueText,
                mb: 6,
                lineHeight: 1.5,
                maxWidth: 1000,
                textAlign: 'left',
              }}
            >
              Iris is the first AI-Native Strategy platform for healthcare, transforming a months-long, reactive process into an{' '}
              <span
                style={{
                  color: lightBlue,
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                instant
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-4px',
                    left: 0,
                    right: 0,
                    height: '3px',
                    background: `linear-gradient(90deg, ${lightBlue} 0%, ${primaryBlue} 100%)`,
                    borderRadius: '2px',
                    animation: 'underlineExpand 0.5s ease-out',
                    '@keyframes underlineExpand': {
                      from: { width: 0 },
                      to: { width: '100%' },
                    },
                  }}
                />
              </span>{' '}
              proactive advantage.
            </Typography>

            {/* Three Feature Boxes with hover effects */}
            <Grid container spacing={4} sx={{ mb: 6 }}>
              {[
                {
                  title: 'Reduce Planning Cycles by 50%+',
                  description: 'Apply real-time, predictive intelligence to internal and market data.',
                },
                {
                  title: 'Model & Simulate Scenarios Instantly',
                  description: 'From site selection to network optimization and M&A, generate detailed business cases in an afternoon.',
                },
                {
                  title: 'Enable Proactive, Predictive, Precise Care Delivery',
                  description: 'The first AI-native platform providing uniquely-tailored strategic recommendations.',
                },
              ].map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Card
                    sx={{
                      p: 3,
                      height: '100%',
                      backgroundColor: cardBg,
                      borderRadius: 4,
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '4px',
                        background: `linear-gradient(90deg, ${primaryBlue} 0%, ${lightBlue} 100%)`,
                        transform: 'scaleX(0)',
                        transformOrigin: 'left',
                        transition: 'transform 0.3s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px)',
                        boxShadow: '0 12px 40px rgba(0, 122, 255, 0.2)',
                        borderColor: primaryBlue,
                        '&::before': {
                          transform: 'scaleX(1)',
                        },
                      },
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: 2,
                        textAlign: 'left',
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${primaryBlue} 0%, ${lightBlue} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          boxShadow: `0 4px 12px rgba(0, 122, 255, 0.3)`,
                        }}
                      >
                        <CheckCircle sx={{ color: 'white', fontSize: 24 }} />
                      </Box>
                      <Box>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: primaryBlue,
                            mb: 1.5,
                            fontSize: '1.1rem',
                          }}
                        >
                          {feature.title}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            color: darkBlue,
                            fontSize: '0.95rem',
                            lineHeight: 1.7,
                          }}
                        >
                          {feature.description}
                        </Typography>
                      </Box>
                    </Box>
                  </Card>
                </Grid>
              ))}
            </Grid>

            {/* CTA Buttons with gradient effects */}
            <Box
              sx={{
                display: 'flex',
                gap: 3,
                justifyContent: 'center',
                flexWrap: 'wrap',
              }}
            >
              <Button
                variant="contained"
                sx={{
                  px: 5,
                  py: 1.8,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${primaryBlue} 0%, #5856D6 100%)`,
                  color: 'white',
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: `0 8px 24px rgba(0, 122, 255, 0.3)`,
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: '-100%',
                    width: '100%',
                    height: '100%',
                    background: 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)',
                    transition: 'left 0.5s ease',
                  },
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 32px rgba(0, 122, 255, 0.4)`,
                    '&::before': {
                      left: '100%',
                    },
                  },
                }}
              >
                See it in Action
              </Button>
              <Button
                variant="outlined"
                sx={{
                  px: 5,
                  py: 1.8,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderColor: primaryBlue,
                  borderWidth: 2,
                  color: primaryBlue,
                  borderRadius: 3,
                  textTransform: 'none',
                  backgroundColor: 'transparent',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#0056CC',
                    color: '#0056CC',
                    backgroundColor: 'rgba(0, 122, 255, 0.05)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px rgba(0, 122, 255, 0.2)`,
                  },
                }}
              >
                Contact Us
              </Button>
            </Box>
          </Box>

          {/* Section 2: Move Beyond Static Planning with side-by-side layout */}
          <Box
            sx={{
              width: { xs: '100%', sm: '90%', md: '60%' },
              mb: 10,
              backgroundColor: cardBg,
              borderRadius: 6,
              p: { xs: 3, sm: 2.4, md: 3.6 },
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              mx: { xs: 'auto', md: 0 },
            }}
          >
            <Grid container spacing={{ xs: 3, md: 6 }} alignItems="center">
              <Grid item xs={12} md={6}>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { xs: '1.1rem', sm: '1.2rem', md: '1.65rem' },
                    fontWeight: 400,
                    fontFamily: 'Garamond, "EB Garamond", serif',
                    color: darkBlueText,
                    mb: { xs: 3, md: 2.4 },
                    lineHeight: 1.5,
                    textAlign: 'left',
                  }}
                >
                  Move beyond static, periodic planning cycles, and save months of time.
                </Typography>
                <Button
                  variant="contained"
                  endIcon={<ArrowForward />}
                  sx={{
                    px: { xs: 4, md: 3 },
                    py: { xs: 1.5, md: 1.08 },
                    fontSize: { xs: '0.9rem', md: '0.6rem' },
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${primaryBlue} 0%, #5856D6 100%)`,
                    color: 'white',
                    borderRadius: 3,
                    textTransform: 'none',
                    boxShadow: `0 8px 24px rgba(0, 122, 255, 0.3)`,
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      transform: 'translateX(4px)',
                      boxShadow: `0 12px 32px rgba(0, 122, 255, 0.4)`,
                      '& .MuiButton-endIcon': {
                        transform: 'translateX(4px)',
                      },
                    },
                    '& .MuiButton-endIcon': {
                      transition: 'transform 0.3s ease',
                    },
                  }}
                >
                  Start Now
                </Button>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& img': {
                      width: '100%',
                      height: 'auto',
                      borderRadius: '16px',
                      boxShadow: '0 20px 60px rgba(0, 122, 255, 0.2)',
                    },
                  }}
                >
                  <img
                    src="/images/healthcare network.png"
                    alt="Healthcare Network"
                  />
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Section 3: Platform Features with modern cards */}
          <Box sx={{ width: '100%', mb: 10 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.75rem' },
                fontWeight: 700,
                color: darkBlue,
                mb: 2,
                textAlign: 'center',
              }}
            >
              Platform Features
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: '1.25rem',
                fontWeight: 600,
                color: primaryBlue,
                mb: 8,
                textAlign: 'center',
              }}
            >
              Your Strategic Advantage in Healthcare Delivery
            </Typography>

            <Grid container spacing={4}>
              {[
                {
                  icon: '🧠',
                  title: 'Tailored & Proactive Guidance',
                  description:
                    'Iris provides custom, proactive recommendations based on integrated financial, clinical, and market data, identifying optimal strategies before issues arise.',
                },
                {
                  icon: '⭐',
                  title: 'AI-Enhanced Scenario Planning',
                  description:
                    'Simulate unlimited scenarios in a 3D geospatial environment. Visualize impacts on patient journeys, market share, and financials to test strategies without risk.',
                },
                {
                  icon: '🌍',
                  title: 'Real-Time Market Intelligence',
                  description:
                    'Access hyperlocal demographics and competitor insights. AI-driven predictions forecast market trends, enabling proactive adjustments to stay ahead.',
                },
                {
                  icon: '📊',
                  title: 'Seamless Data Integration',
                  description:
                    'Secure, live integration with EHR, RCM, and financial systems. Our "plug-and-play" AI-powered engine ensures continuous, HIPAA-compliant data flow.',
                },
              ].map((feature, index) => (
                <Grid item xs={12} md={6} key={index}>
                  <Card
                    sx={{
                      height: '100%',
                      p: 4,
                      borderRadius: 4,
                      backgroundColor: cardBg,
                      border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, ${primaryBlue}15 0%, ${lightBlue}15 100%)`,
                        opacity: 0,
                        transition: 'opacity 0.4s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-12px) scale(1.02)',
                        boxShadow: '0 20px 60px rgba(0, 122, 255, 0.25)',
                        borderColor: primaryBlue,
                        '&::before': {
                          opacity: 1,
                        },
                        '& .feature-icon': {
                          transform: 'scale(1.1) rotate(5deg)',
                        },
                      },
                    }}
                  >
                    <CardContent>
                      <Box
                        className="feature-icon"
                        sx={{
                          width: 80,
                          height: 80,
                          borderRadius: '50%',
                          background: `linear-gradient(135deg, ${primaryBlue} 0%, ${lightBlue} 100%)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mb: 3,
                          fontSize: '40px',
                          boxShadow: `0 8px 24px rgba(0, 122, 255, 0.3)`,
                          transition: 'transform 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: darkBlue,
                          mb: 2,
                          fontSize: '1.3rem',
                        }}
                      >
                        {feature.title}
                      </Typography>
                      <Typography
                        variant="body1"
                        sx={{
                          color: darkBlue,
                          lineHeight: 1.7,
                          fontSize: '0.95rem',
                        }}
                      >
                        {feature.description}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>

          {/* Section 4: The Iris Care Score with dramatic effect */}
          <Box
            sx={{
              width: '100%',
              maxWidth: 700,
              mb: 10,
              mx: 'auto',
              p: { xs: 3, md: 5 },
              backgroundColor: cardBg,
              borderRadius: 4,
              border: `2px solid ${primaryBlue}`,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: `0 8px 32px rgba(0, 122, 255, 0.15)`,
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: '3rem', md: '4rem' },
                  mb: 2,
                  lineHeight: 1,
                }}
              >
                💯
              </Typography>
              <Typography
                variant="h4"
                sx={{
                  fontSize: { xs: '1.25rem', md: '1.5rem' },
                  fontWeight: 600,
                  color: primaryBlue,
                  mb: 1,
                }}
              >
                The Iris Care Score:
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontSize: { xs: '1rem', md: '1.25rem' },
                  fontWeight: 600,
                  color: darkBlue,
                  mb: 3,
                }}
              >
                Revolutionary Measurement
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  color: darkBlue,
                  lineHeight: 1.6,
                  fontSize: '0.95rem',
                  mb: 4,
                  maxWidth: 600,
                  mx: 'auto',
                }}
              >
                Our patent-pending metric objectively evaluates healthcare delivery effectiveness across organizations, facilities, and providers. Combining Quality + Access measures into a singular, dynamic score, it empowers proactive strategic planning as a unique differentiator.
              </Typography>
              <Button
                variant="contained"
                sx={{
                  px: 4,
                  py: 1.5,
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${primaryBlue} 0%, #5856D6 100%)`,
                  color: 'white',
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: `0 4px 16px rgba(0, 122, 255, 0.25)`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: `0 8px 24px rgba(0, 122, 255, 0.35)`,
                  },
                }}
              >
                Learn More
              </Button>
            </Box>
          </Box>

          {/* Section 5: The Bottom Line with animated numbers */}
          <Box ref={bottomLineSectionRef} sx={{ width: '100%', mb: 10 }}>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.75rem' },
                fontWeight: 700,
                color: darkBlue,
                mb: 3,
                textAlign: 'center',
              }}
            >
              The Bottom Line.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: '1.1rem',
                color: darkBlue,
                mb: 8,
                textAlign: 'center',
                maxWidth: 800,
                mx: 'auto',
                opacity: 0.8,
              }}
            >
              Iris empowers healthcare organizations to achieve dramatic improvements in KPIs, directly impacting your bottom line and patient outcomes.
            </Typography>

            <Grid container spacing={4}>
              {[
                {
                  percentage: '10-25%',
                  title: 'Patient Capture Increase',
                  description:
                    'For targeted service lines through optimized access points and referral pathway improvements',
                },
                {
                  percentage: '15-30%',
                  title: 'Operational Cost Reduction',
                  description:
                    'Through elimination of administrative waste and strategic resource reallocation',
                },
                {
                  percentage: '10-15%',
                  title: 'VBC Performance Improvement',
                  description:
                    'Improvement in overall performance under risk-based reimbursement models',
                },
              ].map((metric, index) => {
                const isHighlighted = highlightedCardIndex === index && highlightedCardIndex !== -1;
                return (
                  <Grid item xs={12} md={4} key={index}>
                    <Card
                      sx={{
                        height: '100%',
                        p: 4,
                        textAlign: 'center',
                        backgroundColor: cardBg,
                        borderRadius: 4,
                        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        overflow: 'hidden',
                        transform: isHighlighted ? 'translateY(-8px)' : 'translateY(0)',
                        boxShadow: isHighlighted
                          ? '0 12px 40px rgba(0, 122, 255, 0.2)'
                          : '0 4px 20px rgba(0, 0, 0, 0.08)',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          bottom: 0,
                          left: 0,
                          right: 0,
                          height: '4px',
                          background: `linear-gradient(90deg, ${primaryBlue} 0%, ${lightBlue} 100%)`,
                          transform: isHighlighted ? 'scaleX(1)' : 'scaleX(0)',
                          transformOrigin: 'left',
                          transition: 'transform 0.4s ease',
                        },
                        '&:hover': {
                          transform: 'translateY(-8px)',
                          boxShadow: '0 12px 40px rgba(0, 122, 255, 0.2)',
                          '&::after': {
                            transform: 'scaleX(1)',
                          },
                          '& .percentage': {
                            transform: 'scale(1.1)',
                            color: primaryBlue,
                          },
                        },
                      }}
                    >
                      <Typography
                        className="percentage"
                        variant="h1"
                        sx={{
                          fontSize: { xs: '3rem', md: '4.5rem' },
                          fontWeight: 700,
                          color: lightBlue,
                          mb: 2,
                          transition: 'all 0.3s ease',
                          background: `linear-gradient(135deg, ${lightBlue} 0%, ${primaryBlue} 100%)`,
                          backgroundClip: 'text',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          transform: isHighlighted ? 'scale(1.1)' : 'scale(1)',
                          ...(isHighlighted && {
                            color: primaryBlue,
                          }),
                        }}
                      >
                        {metric.percentage}
                      </Typography>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: darkBlue,
                          mb: 2,
                          fontSize: '1.1rem',
                        }}
                      >
                        {metric.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: darkBlue,
                          lineHeight: 1.7,
                          fontSize: '0.9rem',
                          opacity: 0.8,
                        }}
                      >
                        {metric.description}
                      </Typography>
                    </Card>
                  </Grid>
                );
              })}
            </Grid>
          </Box>

          {/* Section 6: Final CTA Section with gradient background */}
          <Box
            sx={{
              width: '100%',
              mb: 10,
              textAlign: 'left',
              p: { xs: 4, md: 8 },
              borderRadius: 6,
              background: isDark
                ? 'linear-gradient(135deg, rgba(0, 122, 255, 0.1) 0%, rgba(122, 240, 255, 0.05) 100%)'
                : 'linear-gradient(135deg, rgba(0, 122, 255, 0.05) 0%, rgba(122, 240, 255, 0.02) 100%)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 122, 255, 0.1)'}`,
            }}
          >
            <Box
              sx={{
                mb: 4,
                display: 'block',
                animation: 'float 3s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-10px)' },
                },
              }}
            >
              <img
                src="/images/logo.png"
                alt="Iris Logo"
                style={{
                  height: '60px',
                  width: 'auto',
                  objectFit: 'contain',
                  filter: 'drop-shadow(0 4px 12px rgba(0, 122, 255, 0.3))',
                }}
              />
            </Box>
            <Typography
              variant="h2"
              sx={{
                fontSize: { xs: '1.75rem', md: '2.75rem' },
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: darkBlueText,
                mb: 2,
                lineHeight: 1.5,
                textAlign: 'left',
              }}
            >
              The future of care delivery is proactive, predictive, and precise.
            </Typography>
            <Typography
              variant="h3"
              sx={{
                fontSize: { xs: '1.5rem', md: '2.25rem' },
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: darkBlueText,
                mb: 2,
                textAlign: 'left',
              }}
            >
              The future of care delivery is{' '}
              <span
                style={{
                  color: primaryBlue,
                  position: 'relative',
                  display: 'inline-block',
                }}
              >
                Iris
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: '-4px',
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: `linear-gradient(90deg, ${primaryBlue} 0%, ${lightBlue} 100%)`,
                    borderRadius: '2px',
                  }}
                />
              </span>
              .
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontSize: '1.1rem',
                fontWeight: 400,
                fontFamily: 'Garamond, "EB Garamond", serif',
                color: darkBlueText,
                mb: 5,
                textAlign: 'left',
              }}
            >
              See what the first AI-native strategy platform can do for your healthcare organization.
            </Typography>
            <Box
              sx={{
                display: 'flex',
                gap: { xs: 2, md: 3 },
                justifyContent: { xs: 'center', md: 'flex-start' },
                flexWrap: 'wrap',
                mb: 6,
                px: { xs: 2, sm: 0 },
              }}
            >
              <Button
                variant="contained"
                sx={{
                  px: 5,
                  py: 1.8,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: `linear-gradient(135deg, ${primaryBlue} 0%, #5856D6 100%)`,
                  color: 'white',
                  borderRadius: 3,
                  textTransform: 'none',
                  boxShadow: `0 8px 24px rgba(0, 122, 255, 0.3)`,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 32px rgba(0, 122, 255, 0.4)`,
                  },
                }}
              >
                Schedule Demo
              </Button>
              <Button
                variant="outlined"
                sx={{
                  px: 5,
                  py: 1.8,
                  fontSize: '1rem',
                  fontWeight: 600,
                  borderColor: darkBlue,
                  borderWidth: 2,
                  color: darkBlue,
                  borderRadius: 3,
                  textTransform: 'none',
                  backgroundColor: isDark ? 'transparent' : 'white',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: darkBlue,
                    backgroundColor: isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(29, 29, 31, 0.05)',
                    transform: 'translateY(-2px)',
                    boxShadow: `0 8px 24px rgba(0, 0, 0, 0.1)`,
                  },
                }}
              >
                Contact Us
              </Button>
            </Box>
            <Box
              sx={{
                backgroundColor: `rgba(0, 122, 255, 0.1)`,
                borderRadius: 3,
                p: 3,
                maxWidth: 900,
                mx: 'auto',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 2,
                textAlign: 'left',
                border: `1px solid ${primaryBlue}30`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: `rgba(0, 122, 255, 0.15)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 24px rgba(0, 122, 255, 0.2)`,
                },
              }}
            >
              <Info
                sx={{
                  fontSize: '1.5rem',
                  color: primaryBlue,
                  flexShrink: 0,
                  mt: 0.5,
                }}
              />
              <Typography
                variant="body1"
                sx={{
                  color: darkBlue,
                  lineHeight: 1.7,
                  fontSize: '0.95rem',
                }}
              >
                Join healthcare leaders leveraging AI-powered strategic intelligence to capture more opportunities and optimize care delivery.
              </Typography>
            </Box>
          </Box>

          {/* Section 7: Built for Leaders At - STYLIZED WITH EFFECTS */}
          <Box
            ref={leadersSectionRef}
            sx={{
              width: '100%',
              mb: 10,
              position: 'relative',
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: darkBlue,
                mb: 6,
                textAlign: 'center',
                position: 'relative',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-12px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '100px',
                  height: '4px',
                  background: `linear-gradient(90deg, ${primaryBlue} 0%, ${lightBlue} 100%)`,
                  borderRadius: '2px',
                },
              }}
            >
              Built for Leaders At:
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              {[
                { icon: NetworkIcon, text: 'Health Systems & Hospital Networks' },
                { icon: HospitalIcon, text: 'Ambulatory Surgical Centers' },
                { icon: GroupsIcon, text: 'Clinic Networks & Physician Groups' },
                { icon: BusinessIcon, text: 'Accountable Care Organizations' },
                { icon: MedicalServicesIcon, text: 'Primary & Specialty Care Organizations' },
              ].map((item, index) => {
                const IconComponent = item.icon;
                const isVisible = visibleLeaderCards.includes(index);
                return (
                  <Card
                    key={index}
                    sx={{
                      p: 3,
                      backgroundColor: cardBg,
                      borderRadius: 4,
                      border: `2px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)'}`,
                      boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                      position: 'relative',
                      overflow: 'hidden',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      opacity: isVisible ? 1 : 0,
                      transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: `linear-gradient(135deg, ${primaryBlue}15 0%, ${lightBlue}15 100%)`,
                        opacity: 0,
                        transition: 'opacity 0.4s ease',
                      },
                      '&:hover': {
                        transform: 'translateY(-8px) rotate(1deg)',
                        boxShadow: `0 16px 48px rgba(0, 122, 255, 0.25)`,
                        borderColor: primaryBlue,
                        '&::before': {
                          opacity: 1,
                        },
                        '& .leader-icon': {
                          transform: 'scale(1.2) rotate(10deg)',
                          boxShadow: `0 8px 24px rgba(0, 122, 255, 0.4)`,
                        },
                        '& .leader-text': {
                          color: primaryBlue,
                        },
                      },
                    }}
                  >
                    <Box
                      className="leader-icon"
                      sx={{
                        width: 56,
                        height: 56,
                        borderRadius: 3,
                        background: `linear-gradient(135deg, ${primaryBlue} 0%, ${lightBlue} 100%)`,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        boxShadow: `0 4px 12px rgba(0, 122, 255, 0.3)`,
                        transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                        position: 'relative',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          inset: -2,
                          borderRadius: 3,
                          padding: 2,
                          background: `linear-gradient(135deg, ${primaryBlue}, ${lightBlue})`,
                          WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                          WebkitMaskComposite: 'xor',
                          maskComposite: 'exclude',
                          opacity: 0,
                          transition: 'opacity 0.4s ease',
                        },
                      }}
                    >
                      <IconComponent sx={{ fontSize: 28, color: 'white' }} />
                    </Box>
                    <Typography
                      className="leader-text"
                      variant="body1"
                      sx={{
                        fontWeight: 400,
                        fontFamily: 'Garamond, "EB Garamond", serif',
                        color: darkBlueText,
                        fontSize: '1rem',
                        transition: 'color 0.3s ease',
                      }}
                    >
                      {item.text}
                    </Typography>
                  </Card>
                );
              })}
            </Box>
          </Box>
        </Box>
      </Container>

      {/* Footer with Copyright */}
      <Box
        sx={{
          py: 4,
          borderTop: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
          backgroundColor: bgColor,
          textAlign: 'center',
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="body2"
            sx={{
              color: darkBlueText,
              fontSize: '0.9rem',
              fontFamily: 'Garamond, "EB Garamond", serif',
              fontWeight: 400,
            }}
          >
            © 2025 Iris Technology Solutions
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;
